import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { OrganizationsRef, User } from "../models/User";
import UsersService from "../service/users.service";
import alertService from "../components/alert/alertService";
import { t } from "i18next";
import { handleApiError } from "../api/handleApiError";
import secureLocalStorage, {
  clearSessionKeys,
  clearSessionScope,
  scopeSessionToBrowser,
} from "../utils/secureLocalStorage";
import { STORAGE_KEYS } from "../constants";

export interface AuthContextType {
  user: User | null;
  organization: OrganizationsRef | null;
  isInitialized: boolean; // ✅ 1. เพิ่ม Flag บอกว่าโหลดข้อมูลเสร็จหรือยัง
  loginCooldownSeconds: number;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<void>;
  logout: () => Promise<void>;
  selectOrganization: (organization: OrganizationsRef | null) => void;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  getUserInfo: () => Promise<void>;
  isDataEntry: () => boolean | undefined;
  updateUserData: (user: User) => void;
  // ล้าง session ในเครื่องอย่างเดียว ไม่ revoke ฝั่ง server — ใช้ตอนที่ token ค้างอาจเป็นของคนเดียวกับที่กำลังจะ sign in
  clearLocalSession: () => void;
  // ใช้ตอนหน้า /auth/sso ได้ user object กลับมาจาก /v1/app/sso/exchange แล้ว
  // ไม่ต้องเรียก /v1/users/login ซ้ำ และ error handling เป็นหน้าที่ของ landing page เอง
  loginFromSso: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const LOGOUT_TIMEOUT_MS = 5000;

// เผื่อ backend ค้างไม่ตอบ — race กับ timeout กันไม่ให้ logout ค้างตามไปด้วย
const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("LOGOUT_TIMEOUT")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<OrganizationsRef | null>(
    null,
  );
  // ✅ 2. เพิ่ม State สำหรับตรวจสอบสถานะการโหลดข้อมูลเริ่มต้น
  const [isInitialized, setIsInitialized] = useState(false);
  const [loginCooldownSeconds, setLoginCooldownSeconds] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAuthenticated = () => {
    return !!user;
  };

  // ✅ 3. ปรับ useEffect ให้ทำงานเสร็จแล้วค่อย set isInitialized = true
  useEffect(() => {
    const initAuth = () => {
      const storedUser = secureLocalStorage.getJSON<User>(STORAGE_KEYS.USER);
      const storedOrganization = secureLocalStorage.getJSON<OrganizationsRef>(STORAGE_KEYS.ORG);

      if (storedUser) setUser(storedUser);
      if (storedOrganization) setOrganization(storedOrganization);

      // โหลดข้อมูลเสร็จแล้ว อนุญาตให้ App ทำงานต่อได้
      setIsInitialized(true);
    };

    initAuth();
  }, []);

  const startCooldown = (seconds: number) => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    setLoginCooldownSeconds(seconds);
    cooldownTimerRef.current = setInterval(() => {
      setLoginCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current!);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, []);

  const updateUserData = (newUser: User) => {
    setUser(newUser);
    secureLocalStorage.setJSON(STORAGE_KEYS.USER, newUser);
  };

  const handleError = (error: unknown) => {
    const { code, retryAfter } = (error as any)?.response?.data ?? {};

    if (
      code === "ACCOUNT_LOCKED_COOLDOWN" ||
      code === "ACCOUNT_LOCKED_COOLDOWN_EMAIL_SENT"
    ) {
      startCooldown(retryAfter ?? 30);
    } else if (code === "ACCOUNT_LOCKED_HARD") {
      startCooldown(retryAfter ?? 86400);
    }

    handleApiError(
      error,
      {
        VALIDATION_ERROR: "Must-be-a-valid-email",
        USER_NOT_FOUND: "incorrect-email-or-password",
        WRONG_PASSWORD: "incorrect-email-or-password",
        USER_NOT_ACTIVATED: "Please-activate-your-account",
        ACCOUNT_LOCKED_COOLDOWN: "too-many-login-attempts",
        ACCOUNT_LOCKED_COOLDOWN_EMAIL_SENT: "Account-Temporarily-Restricted",
        ACCOUNT_LOCKED_HARD: "account-hard-locked",
      },
      "cannot-connect-to-server",
    );
  };

  // SSO landing page เรียกหลัง /v1/app/sso/exchange สำเร็จ — มี user object พร้อม
  // accessToken/refreshToken มาแล้ว ไม่ต้องยิง /v1/users/login ซ้ำ
  const loginFromSso = (newUser: User) => {
    setUser(newUser);
    secureLocalStorage.setJSON(STORAGE_KEYS.USER, newUser);
  };

  const login = async (email: string, password: string, rememberMe = true) => {
    try {
      alertService.showLoading();
      const userData = await UsersService.loginUser(email, password);

      alertService.closeLoading();

      // scope first: a session scoped without its marker reads as an already-closed browser
      if (rememberMe) {
        clearSessionScope();
      } else {
        scopeSessionToBrowser();
      }

      setUser(userData);

      secureLocalStorage.setJSON(STORAGE_KEYS.USER, userData);
    } catch (error) {
      alertService.closeLoading();
      setUser(null);
      secureLocalStorage.removeItem(STORAGE_KEYS.USER);
      secureLocalStorage.removeItem(STORAGE_KEYS.ORG);
      try { handleError(error); } catch { /* alert already shown — prevent unhandled rejection */ }
    }
  };

  const clearState = useCallback(() => {
    setUser(null);
    setOrganization(null);
  }, []);

  // shared with the startup check, so a restarted browser leaves the origin exactly as a sign-out does
  const clearStorage = useCallback(() => clearSessionKeys(), []);

  const clearSession = useCallback(() => {
    clearState();
    clearStorage();
  }, [clearState, clearStorage]);

  // กันเรียกซ้ำตอนรอ request เดิม (กด Sign Out ซ้ำ หรือหน้า activate/auth-link เรียกซ้อน) — ไม่งั้นจะยิง POST ซ้ำก่อน storage เคลียร์
  const logoutInFlightRef = useRef(false);

  const logout = useCallback(async () => {
    if (logoutInFlightRef.current) return;

    // ไม่มี session ให้ revoke (เช่น เรียกซ้ำหลัง clear ไปแล้ว) — เคลียร์เฉยๆ ไม่ยิง request/เตือน
    const currentUser = secureLocalStorage.getJSON<User>(STORAGE_KEYS.USER);
    if (!currentUser) {
      clearSession();
      return;
    }

    logoutInFlightRef.current = true;
    // เคลียร์ state ก่อนยิง request — ไม่งั้น LoginLayout เห็น user ไม่ null แล้วเด้งกลับเข้าแอป
    clearState();

    try {
      // ต้องยิงก่อนเคลียร์ storage — request ต้องใช้ access token เดิมตอน authenticate
      await withTimeout(UsersService.logoutUser(), LOGOUT_TIMEOUT_MS);
    } catch (error) {
      // TOKEN_REVOKED: server ไม่รับ token นี้แล้ว ไม่มีอะไรให้ออกด้วย token นี้อีก — ไม่ต้องเตือน; 401 อื่นยังยืนยันไม่ได้
      const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
      if (!(response?.status === 401 && response.data?.code === "TOKEN_REVOKED")) {
        // อาจสำเร็จที่ server แล้วแต่ response มาไม่ทันก็ได้ ห้ามฟันธงว่า "ล้มเหลว"
        alertService.mixin(t("logout-uncertain-notice"), "warning");
      }
    } finally {
      clearStorage();
      logoutInFlightRef.current = false;
    }
  }, [clearSession, clearState, clearStorage]);

  const selectOrganization = (organization: OrganizationsRef | null) => {
    setOrganization(organization);
    if (organization) {
      secureLocalStorage.setJSON(STORAGE_KEYS.ORG, organization);
    } else {
      secureLocalStorage.removeItem(STORAGE_KEYS.ORG);
    }
  };

  const isAdmin = (): boolean => {
    if (!user || !organization) return false;

    const role = user.authorization?.find((i) => {
      return i.organization === organization.id;
    })?.role;

    return role === 1;
  };

  const isDataEntry = (): boolean | undefined => {
    if (!user || !organization) return undefined;

    const role = user.authorization?.find((i) => {
      return i.organization === organization.id;
    })?.role;

    return role === 3;
  };

  const getUserInfo = async () => {
    try {
      const userInfo: User = await UsersService.fetchUserInfo();

      if (user) {
        const updatedUser: User = {
          ...user,
          ...userInfo,
          userProfile: userInfo.userProfile,
          fullname: userInfo.fullname,
          displayName: userInfo.displayName,
          phoneNumber: userInfo.phoneNumber,
          jobTitle: userInfo.jobTitle,
        };

        setUser(updatedUser);
        secureLocalStorage.setJSON(STORAGE_KEYS.USER, updatedUser);
      }
    } catch (error) {
      try {
        handleApiError(
          error,
          {},
          { messageKey: "error-get-user", titleKey: "Error-Message" },
        );
      } catch { /* alert already shown — prevent unhandled rejection */ }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        organization,
        selectOrganization,
        isAuthenticated,
        isAdmin,
        getUserInfo,
        isDataEntry,
        updateUserData,
        clearLocalSession: clearSession,
        isInitialized, // ✅ 4. ส่งค่า isInitialized ออกไปให้ ProtectedRoutes ใช้
        loginCooldownSeconds,
        loginFromSso,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
